/* eslint-disable max-len */
import Dexie from 'dexie';
import moment from 'moment';
import { captureException } from './logService';
const DB_VERSION_NUMBER: number = 32;

export const FILES_TABLE_NAME: string = 'Files';
export const TASKS_TABLE_NAME: string = 'Tasks';
export const TIME_TRACKING_NAME: string = 'Time-Tracking';
export const PROJECT_NAME: string = 'Project';
export const CHAT_MESSAGE_TABLE_NAME = 'Chat-Message';
export const MY_TIME_LOG_NAME: string = 'My-Time-Log';
export const MESSAGE_GROUP_TABLE_NAME = 'Message-Group';
export const UPDATED_ON_TABLE_NAME = 'UpdatedOn';
export const DRAFT_MESSAGES = 'Draft-Message';

// Mobx
export const TASKS_TABLE_NAME_MOBX = 'Tasks-Mobx';
export const PROJECTS_TABLE_NAME_MOBX = 'Projects-Mobx';
export const FILES_TABLE_NAME_MOBX = 'Files-Mobx';

export const UPDATE_ON_ID = {
  FILES: 'FILES',
  TASKS: 'TASKS',
  TIME_TRACKING: 'TIME_TRACKING',
  PROJECT: 'PROJECT',
  CHAT_MESSAGE: 'CHAT_MESSAGE',
  MESSAGE_GROUP: 'MESSAGE_GROUP',
  MY_TIME_LOG: 'MY_TIME_LOG',
  DRAFT_MESSAGE: 'DRAFT_MESSAGE'
};

export default class LocalDbService {
  localDb: any;
  props: any;
  db: any;
  private connectionAttempts: number = 0;
  private readonly MAX_RETRY_ATTEMPTS: number = 3;
  private readonly RETRY_DELAY: number = 1000; // 1 second

  constructor(props: any) {
    this.props = props;
    const db_name = props.dbName;
    this.db = new Dexie(db_name);
    const storeOptions: any = {};
    storeOptions[FILES_TABLE_NAME] = '&id,tableId,value';
    storeOptions[TASKS_TABLE_NAME] = '&id,projectId,status,value';
    storeOptions[TIME_TRACKING_NAME] = '&id,date,createdAt,projectId,value';
    storeOptions[PROJECT_NAME] = '&id,tableId,value';
    storeOptions[CHAT_MESSAGE_TABLE_NAME] = '&id,projectId,messageGroupId,value';
    storeOptions[MESSAGE_GROUP_TABLE_NAME] = '&id,value';
    storeOptions[UPDATED_ON_TABLE_NAME] = '&id,createdAt,value';
    storeOptions[MY_TIME_LOG_NAME] = '&id,date,createdAt,projectId,value';
    storeOptions[DRAFT_MESSAGES] = '&id,value';
    storeOptions[TASKS_TABLE_NAME_MOBX] = '&id,projectId';
    storeOptions[PROJECTS_TABLE_NAME_MOBX] = '&id,isFavorite';
    storeOptions[FILES_TABLE_NAME_MOBX] = '&id,projectId';

    this.db
      .version(DB_VERSION_NUMBER)
      .stores(storeOptions)
      .upgrade((trans: any) => {
        if (DB_VERSION_NUMBER <= 32) {
          const clearDbData = async () => {
            await this.clearDbData();
            window.location.reload();
          };
          clearDbData();
        }
      });
  }

  private async wait(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async initializeDatabaseConnection() {
    try {
      if (!this.db) {
        this.db = new Dexie(this.props.dbName);
        // Re-initialize the database schema
        const storeOptions: any = {};
        storeOptions[FILES_TABLE_NAME] = '&id,tableId,value';
        storeOptions[TASKS_TABLE_NAME] = '&id,projectId,status,value';
        storeOptions[TIME_TRACKING_NAME] = '&id,date,createdAt,projectId,value';
        storeOptions[PROJECT_NAME] = '&id,tableId,value';
        storeOptions[CHAT_MESSAGE_TABLE_NAME] = '&id,projectId,messageGroupId,value';
        storeOptions[MESSAGE_GROUP_TABLE_NAME] = '&id,value';
        storeOptions[UPDATED_ON_TABLE_NAME] = '&id,createdAt,value';
        storeOptions[MY_TIME_LOG_NAME] = '&id,date,createdAt,projectId,value';
        storeOptions[DRAFT_MESSAGES] = '&id,value';
        storeOptions[TASKS_TABLE_NAME_MOBX] = '&id,projectId';
        storeOptions[PROJECTS_TABLE_NAME_MOBX] = '&id,isFavorite';
        storeOptions[FILES_TABLE_NAME_MOBX] = '&id,projectId';

        this.db.version(DB_VERSION_NUMBER).stores(storeOptions);
      }

      if (this.db.isOpen() === false) {
        this.connectionAttempts = 0;
        while (this.connectionAttempts < this.MAX_RETRY_ATTEMPTS) {
          try {
            await this.db.open();
            return true;
          } catch (error) {
            this.connectionAttempts += 1;
            if (this.connectionAttempts >= this.MAX_RETRY_ATTEMPTS) {
              throw error;
            }
            await this.wait(this.RETRY_DELAY);
          }
        }
      }
      return true;
    } catch (error) {
      captureException(error);
      // If we've exhausted all retries, try to recreate the database
      if (this.connectionAttempts >= this.MAX_RETRY_ATTEMPTS) {
        try {
          await this.db.delete();
          this.db = new Dexie(this.props.dbName);
          await this.initializeDatabaseConnection();
          return true;
        } catch (recreateError) {
          captureException(recreateError);
          return false;
        }
      }
      return false;
    }
  }

  getDb() {
    const tableName = this.props.tableName;
    if (this.db) return this.db[tableName];
  }

  closeDb() {
    if (this.db) {
      this.db.close();
      this.db = undefined;
    }
  }

  static async deleteDatabase() {
    try {
      const databases = await Dexie.getDatabaseNames();
      for (let i = 0; i < databases?.length; i++) {
        let dbName = databases[i];
        if (dbName !== 'firebaseLocalStorageDb' && dbName !== 'firebase-heartbeat-database') await Dexie.delete(dbName);
      }
      return true;
    } catch (error) {
      captureException(error);
      return false;
    }
  }

  static async openDatabase() {
    try {
      const databases = await Dexie.getDatabaseNames();
      for (const databaseName of databases) {
        const db = new Dexie(databaseName);
        await db.close();
      }
      return true;
    } catch (error) {
      captureException(error);
      return false;
    }
  }

  getMappedValues(list: any) {
    const dbItems = list
      ?.filter((x: any) => x?.id)
      ?.map((x: any) => {
        const item: any = {
          id: x.id,
          createdAt: x.createdAt,
          value: x
        };
        if (x.status) item.status = x.status;
        if (x.date) item.date = x.date;
        if (this.props.tableName === TASKS_TABLE_NAME) {
          item.projectId = x.projectId;
          item.createdAt = x.createdOn;
          item.status = x.status;
        }
        if (this.props.tableName === TIME_TRACKING_NAME || this.props.tableName === MY_TIME_LOG_NAME) {
          item.createdAt = x.createdOn;
          item.projectId = x.projectId;
          item.date = x?.date;
        }
        if (this.props.tableName === FILES_TABLE_NAME || this.props.tableName === PROJECT_NAME) item.tableId = x.id;

        if (this.props.tableName === CHAT_MESSAGE_TABLE_NAME) {
          item.messageGroupId = x?.messageGroupId;
          item.projectId = x?.projectId;
        }
        if (this.props.tableName === MESSAGE_GROUP_TABLE_NAME) {
          item.createdAt = x.createdOn;
        }
        if (this.props.tableName === DRAFT_MESSAGES) {
          item.type = x.type;
        }
        return item;
      });
    return dbItems || [];
  }

  async get() {
    try {
      if (this.db) {
        await this.initializeDatabaseConnection();
        const rows = await this.getDb().toArray();
        const result = rows.map((x: any) => x.value).filter((x: any) => x);
        return result;
      }
      return [];
    } catch (error) {
      captureException(error);
      return [];
    }
  }

  async getItem(id: any) {
    try {
      if (this.db) {
        await this.initializeDatabaseConnection();
        const rows = await this.getDb().where({ id: id }).limit(1).toArray();
        if (rows && rows.length > 0) {
          const result = rows.map((x: any) => x.value).filter((x: any) => x);
          return result[0];
        }
      }
      return null;
    } catch (error) {
      captureException(error);
      return null;
    }
  }

  async clearDbData() {
    if (this.db) {
      await this.getDb().clear();
      await this.db[FILES_TABLE_NAME].clear();
      await this.db[TASKS_TABLE_NAME].clear();
      await this.db[TIME_TRACKING_NAME].clear();
      await this.db[PROJECT_NAME].clear();
      await this.db[CHAT_MESSAGE_TABLE_NAME].clear();
      await this.db[MESSAGE_GROUP_TABLE_NAME].clear();
      await this.db[UPDATED_ON_TABLE_NAME].clear();
      await this.db[MY_TIME_LOG_NAME].clear();
      await this.db[DRAFT_MESSAGES].clear();
      await this.db[TASKS_TABLE_NAME_MOBX].clear();
      await this.db[PROJECTS_TABLE_NAME_MOBX].clear();
      await this.db[FILES_TABLE_NAME_MOBX].clear();
      return true;
    }
    return false;
  }

  async addBulk(items: any) {
    if (this.db) {
      const result = await this.getDb().bulkPut(this.getMappedValues(items));
      return result;
    }
    return false;
  }

  async removeBulk(items: any) {
    if (this.db) {
      const result = await this.getDb().bulkDelete(items);
      return result;
    }
    return false;
  }

  async updateExisting(id: any, value: any) {
    if (value && this.db) {
      const dbItem = {
        id: id,
        tableId: value.tableId,
        value: value
      };
      const result = await this.getDb().update(id, dbItem);
      return result;
    }
    return null;
  }

  async getLastUpdatedTime(id: any) {
    try {
      if (this.db) {
        const first = await this.db[UPDATED_ON_TABLE_NAME].where('id').equals(id).first();
        return first?.value || 0;
      }
      return 0;
    } catch (e) {
      captureException(e);
      return 0;
    }
  }

  async setLastUpdatedTime(id: string, value: any) {
    try {
      if (value && this.db) {
        const result = await this.db[UPDATED_ON_TABLE_NAME].put({ id: id, value: value });
        return result;
      }
      return null;
    } catch (error) {
      captureException(error);
    }
  }

  async syncList(newList: any, archivedList: any) {
    if (this.db) {
      //Add/update list only if have list of new/archived
      if (newList && newList.length > 0) {
        await this.addBulk(newList);
      }
      if (archivedList && archivedList.length > 0) {
        await this.removeBulk(archivedList);
      }
    }
  }

  async clearOlderThanThreeMonthsLocalData(statusArr: any) {
    const daysToClearOlderData = 90;
    const dateBeforeNDays = new Date(moment().subtract(daysToClearOlderData, 'days').toISOString()).toISOString();
    await this.getDb()
      .where('date')
      .belowOrEqual(dateBeforeNDays)
      .and((item: any) => (statusArr?.length === 0 ? true : statusArr?.includes(item?.status)))
      .delete();
  }
}
